import React from "react";
import { bool, string, arrayOf, shape } from "prop-types";
import { Box, Stack, Paper, Typography } from "@mui/material";
import { useParams, useLocation } from "react-router";
import ProgressIndicator from "./ProgressIndicator";
import ReportSnapshotCard from "./ReportSnapshotCard";

import { connectToState } from "../redux/connect-api";
import {
  searchStringSelector,
  activeDetailIdSelector,
  analysesLoadingSelector,
  getReportSnapshotCollection,
} from "../selectors";
import { menu } from "../component-styles";
import ReportSnapshot from "./AppPages/ReportSnapshot";
import { trackPageView } from "../hooks/usePageView";
import { userIsTrialSubscriberSelector } from "../selectors/userProfile";

// Wrapper component to inject router hooks
const ReportSnapshotsWithRouter = (props) => {
  const params = useParams();
  const location = useLocation();
  return <ReportSnapshots {...props} params={params} location={location} />;
};

class ReportSnapshots extends React.PureComponent {
  componentDidMount() {
    !this.props.loading && trackPageView("Report Snapshots");
  }

  render() {
    const {
      loading,
      reportSnapshots,
      searchString = "",
      activeDetailId,
      subscriptionIsTrial,
    } = this.props;

    if (activeDetailId) {
      return <ReportSnapshot reportId={activeDetailId} />;
    }

    if (loading) {
      return (
        <Box sx={{ display: "flex", width: "100%", flexDirection: "column" }}>
          <Paper sx={menu.menu} elevation={0} id="utility_bar" />
          <Box
            sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}
          >
            <ProgressIndicator active={loading} />
          </Box>
        </Box>
      );
    }

    if (!reportSnapshots.length && searchString.length) {
      return (
        <Box
          sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}
          py={6}
        >
          <Typography>
            We weren&apos;t able to find a Report Snapshot matching &apos;{searchString}&apos;
          </Typography>
        </Box>
      );
    }

    const searchResults = () => {
      let searchResults = "";
      if (searchString.length) {
        searchResults = ` matching: ${searchString}`;
      }
      return searchResults;
    };

    return (
      <Box
        sx={{
          display: "flex",
          flex: "1 1 auto",
          flexDirection: "column",
          minHeight: 0,
          width: "100%",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        <Paper
          sx={{ ...menu.menu, alignContent: "start", justifyContent: "start", px: 3 }}
          elevation={0}
          id="utility_bar"
        >
          <Typography>
            {reportSnapshots.length} report snapshot(s) found{searchResults()}
          </Typography>
        </Paper>
        <Box sx={{ padding: 3, flexGrow: 1 }}>
          <Stack
            direction="row"
            sx={{
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            {reportSnapshots.map((report, i) => (
              <Box
                key={i}
                sx={{
                  flex: {
                    xs: "0 0 100%",
                    sm: "0 0 calc(50% - 8px)",
                    lg: "0 0 calc(33.333% - 10.67px)",
                  },
                  display: "flex",
                  alignItems: "stretch",
                  minWidth: 0,
                }}
              >
                <ReportSnapshotCard
                  subscriptionIsTrial={subscriptionIsTrial}
                  attributes={report}
                  searchString={searchString}
                />
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>
    );
  }
}

ReportSnapshots.propTypes = {
  loading: bool,
  searchString: string,
  reportSnapshots: arrayOf(shape({})),
  activeDetailId: string,
  params: shape({
    detailId: string,
    activeTabKey: string,
  }),
  location: shape({
    pathname: string.isRequired,
    search: string.isRequired,
  }).isRequired,
  subscriptionIsTrial: bool,
};

const mapStateToProps = (state, ownProps) => {
  return {
    reportSnapshots: getReportSnapshotCollection(state, ownProps),
    activeDetailId: activeDetailIdSelector(state, ownProps),
    searchString: searchStringSelector(state, ownProps),
    loading: analysesLoadingSelector(state, ownProps),
    subscriptionIsTrial: userIsTrialSubscriberSelector(state),
  };
};

export default connectToState(mapStateToProps)(ReportSnapshotsWithRouter);
